// src/app/interceptors/duplicate-request.interceptor.ts
import { HttpInterceptorFn } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap, shareReplay, finalize } from 'rxjs/operators';

// ✅ Global map to track pending requests
const pendingRequests = new Map<string, Observable<any>>();

/**
 * Functional HTTP Interceptor to prevent duplicate API calls
 * Works with the modern provideHttpClient() approach
 */
export const duplicateRequestInterceptor: HttpInterceptorFn = (req, next) => {
  // Only cache GET requests to prevent duplicate data fetching
  if (req.method !== 'GET') {
    return next(req);
  }

  // Create unique key for this request
  const key = getRequestKey(req);
  
  // ✅ If identical request is already pending, return the existing observable
  if (pendingRequests.has(key)) {
    console.log('🚫 Blocking duplicate request:', key);
    return pendingRequests.get(key)!;
  }

  console.log('✅ Allowing new request:', key);

  // ✅ Create new request observable and cache it
  // shareReplay(1): late subscribers (e.g. duplicate GET) still receive the last response;
  // plain share() completes without replaying, so blocked duplicates never got next() → retry storms.
  const request$ = next(req).pipe(
    tap({
      next: (event) => {
        if (event.type === 4) {
          console.log('Request completed:');
        }
      },
      error: (error) => {
        console.log('❌ Request failed:', key, error.status);
      }
    }),
    shareReplay({ bufferSize: 1, refCount: false }),
    finalize(() => {
      setTimeout(() => {
        pendingRequests.delete(key);
        console.log(' Cleaned up request cache for:', key);
      }, 100);
    })
  );

  // Cache the request
  pendingRequests.set(key, request$);
  return request$;
};

/**
 * Generate a unique key for HTTP requests
 * Includes method, URL, and query parameters
 */
function getRequestKey(req: any): string {
  const params = req.params.toString();
  const url = req.url;
  return `${req.method}:${url}${params ? '?' + params : ''}`;
}

/**
 * Utility function to manually clear the request cache
 * Useful for testing or forced cache invalidation
 */
export function clearRequestCache(): void {
  pendingRequests.clear();
  console.log('🧹 Request cache cleared manually');
}

/**
 * Utility function to get current cache size
 * Useful for debugging
 */
export function getRequestCacheSize(): number {
  return pendingRequests.size;
}

/**
 * Utility function to check if a specific request is cached
 * Useful for debugging
 */
export function isRequestCached(method: string, url: string): boolean {
  const key = `${method}:${url}`;
  return pendingRequests.has(key);
}